import { useEffect, useState } from "react";
import { Save, ExternalLink } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import ImageUploader from "@/components/admin/ImageUploader";
import { toast } from "sonner";

const GROUPS = [
  { title: "Home · Hero", slots: [
    ["home_hero_side", "Hero Side Photo", "900×1200 · 3:4"],
    ["home_hero_dress", "Hero Dress Sketch", "800×1000"],
    ["brand_mark", "Brand Mark / Botanical Sketch", "used in footer, hero & cart accents"],
  ]},
  { title: "Home · Process Flow", slots: [
    ["process_1", "Step 1 — Deadstock Fabric", "300×300"],
    ["process_2", "Step 2 — Rescued", "300×300"],
    ["process_3", "Step 3 — Reimagined", "300×300"],
    ["process_4", "Step 4 — Worn By You", "300×300"],
  ]},
  { title: "Home · Deadstock Icons", slots: [
    ["deadstock_1", "Icon 1 — Leftover Fabric", "200×200"],
    ["deadstock_2", "Icon 2 — Not Used", "200×200"],
    ["deadstock_3", "Icon 3 — Rescued", "200×200"],
    ["deadstock_4", "Icon 4 — Reimagined", "200×200"],
  ]},
  { title: "Home · Editorial", slots: [
    ["editorial_portrait", "Our Story Portrait", "800×1000"],
  ]},
  { title: "Home · Category Hover Icons", slots: [
    ["cat_new-arrivals", "New Arrivals", "300×300"],
    ["cat_resort-sets", "Resort Sets", "300×300"],
    ["cat_dresses", "Dresses", "300×300"],
    ["cat_swim", "Swim", "300×300"],
    ["cat_cover-ups", "Cover-Ups", "300×300"],
    ["cat_one-of-one", "One-of-One", "300×300"],
  ]},
  { title: "Lookbook Gallery", slots: [
    ["lookbook_1", "Photo 1 — Amalfi in bloom", "1000×1400"],
    ["lookbook_2", "Photo 2 — Mykonos linen", "1000×1000"],
    ["lookbook_3", "Photo 3 — Seafoam days", "1000×1000"],
    ["lookbook_4", "Photo 4 — Tulum set", "1000×1400"],
    ["lookbook_5", "Photo 5 — Santorini wrap", "1000×1000"],
    ["lookbook_6", "Photo 6 — One of one", "1000×1000"],
    ["lookbook_7", "Photo 7 — Golden hour", "1000×1400"],
    ["lookbook_8", "Photo 8 — Riviera swim", "1000×1000"],
    ["lookbook_9", "Photo 9 — Off-duty white", "1000×1000"],
  ]},
];

export default function AdminPageImages() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/settings/page_images").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const save = async () => {
    try { await api.put("/admin/settings/page_images", data); toast.success("Page images saved."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const origin = window.location.origin;
  const hrefFor = (key) => {
    if (key.startsWith("lookbook_")) return "/lookbook";
    if (key.startsWith("journal_")) return "/journal/" + key.replace("journal_", "");
    if (key.startsWith("cat_")) return "/shop?category=" + key.replace("cat_", "");
    if (key.startsWith("process_")) return "/#process";
    if (key.startsWith("deadstock_")) return "/#deadstock";
    if (key === "editorial_portrait") return "/#editorial";
    return "/";
  };

  if (!data) return <p className="font-body text-sm text-ink/40">Loading…</p>;

  return (
    <div data-testid="admin-page-images">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl">Page Images</h1>
          <p className="font-body text-sm text-ink/50 mt-1">Swap any image across the storefront. Leaving one empty falls back to the original.</p>
        </div>
        <button onClick={save} className="adm-btn" data-testid="save-page-images"><Save size={14} /> Save All</button>
      </div>

      <div className="space-y-10">
        {GROUPS.map((g) => (
          <div key={g.title}>
            <p className="adm-label mb-5 border-b border-ink/10 pb-2">{g.title}</p>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-8">
              {g.slots.map(([key, label, rec]) => (
                <div key={key} className="space-y-2" data-testid={`pi-slot-${key}`}>
                  <ImageUploader testId={`pi-${key}`} label={label} recommended={rec} value={data[key] || ""} onChange={(v) => set(key, v)} />
                  <input className="adm-input text-xs" placeholder="Alt text — describe the image for SEO & screen readers" value={data[`${key}_alt`] || ""} onChange={(e) => set(`${key}_alt`, e.target.value)} data-testid={`pi-${key}-alt`} />
                  <a href={`${origin}${hrefFor(key)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wide2 text-ink/45 hover:text-ink transition-colors" data-testid={`pi-${key}-view`}><ExternalLink size={11} /> View on site</a>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 border-t border-ink/10 pt-6">
        <button onClick={save} className="adm-btn" data-testid="save-page-images-bottom"><Save size={14} /> Save All Changes</button>
      </div>
    </div>
  );
}
